'use client'

import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { queryCachePolicy } from '@/shared/state/query-cache-policy'
import { searchShopProductsAction } from '../actions/shop.actions'
import { hasMeaningfulSearchWords } from '../lib/shop-search-words'
import type { ShopProduct } from '../types/shop.types'

interface UseShopProductsOptions {
  limit?: number
  enabled?: boolean
  /** Owns the one debounce — callers pass the raw input value. */
  debounceMs?: number
  /** Server-read seed for the first paint (results page); see ARCHITECTURE § Server reads. */
  initialData?: ShopProduct[]
}

export const shopProductsQueryKey = (query: string, limit: number) =>
  ['shop', 'products', query.trim(), limit] as const

/**
 * Shop products matching a query, through the Shop's public products API.
 * Keeps the previous keystroke's rows on screen while the next fetch runs, and
 * never fires for a query the Shop would answer with nothing.
 */
export function useShopProducts(query: string, options?: UseShopProductsOptions) {
  const limit = options?.limit ?? 18
  const enabled = options?.enabled ?? true
  const debounceMs = options?.debounceMs ?? 200
  const [debounced, setDebounced] = useState(query)

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), debounceMs)
    return () => clearTimeout(id)
  }, [query, debounceMs])

  const trimmed = debounced.trim()
  const result = useQuery<ShopProduct[]>({
    queryKey: shopProductsQueryKey(trimmed, limit),
    queryFn: async () => {
      const res = await searchShopProductsAction(trimmed, limit)
      if (!res.success) throw new Error(res.error.userMessage)
      return res.data
    },
    enabled: enabled && hasMeaningfulSearchWords(trimmed),
    staleTime: queryCachePolicy.app.staleTime,
    placeholderData: (previousData) => previousData,
    initialData: options?.initialData,
    retry: 1,
  })
  /** `query` says which (debounced) input the rows answer. */
  return { ...result, query: trimmed }
}
